import { PaginatedResponse } from "types/index";

type PaginationOptions = {
  page: number;
  limit: number;
  fallbackMessage?: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;
}

function readItems(source: Record<string, unknown>): unknown[] | null {
  const items = source.items ?? source.Items;
  return Array.isArray(items) ? items : null;
}

function readTotal(source: Record<string, unknown>): number | null {
  const total = source.totalCount ?? source.TotalCount ?? source.total;
  return typeof total === "number" ? total : null;
}

export function normalizePaginatedResponse<T>(
  response: unknown,
  { page, limit, fallbackMessage = "Failed to load data" }: PaginationOptions
): PaginatedResponse<T> {
  const res = asRecord(response);
  if (!res) {
    throw new Error(fallbackMessage);
  }

  // Already-normalized PaginatedResponse nested under ApiResponse.data
  const nested = asRecord(res.data);
  if (
    nested &&
    res.success !== false &&
    Array.isArray(nested.data) &&
    asRecord(nested.pagination)
  ) {
    return nested as unknown as PaginatedResponse<T>;
  }

  // Flat { items|Items, totalCount|TotalCount }
  const flatItems = readItems(res);
  const flatTotal = readTotal(res);
  if (flatItems && flatTotal != null) {
    return {
      data: flatItems as T[],
      pagination: {
        page,
        limit,
        total: flatTotal,
        totalPages: Math.max(1, Math.ceil(flatTotal / limit)),
      },
    };
  }

  // Nested { data: { items|Items, totalCount|TotalCount } }
  if (nested) {
    const nestedItems = readItems(nested);
    const nestedTotal = readTotal(nested);
    if (nestedItems && nestedTotal != null) {
      return {
        data: nestedItems as T[],
        pagination: {
          page,
          limit,
          total: nestedTotal,
          totalPages: Math.max(1, Math.ceil(nestedTotal / limit)),
        },
      };
    }
  }

  const data = (res.data as T[] | undefined) ?? response;
  if (Array.isArray(data)) {
    return {
      data: data as T[],
      pagination: { page, limit, total: data.length, totalPages: 1 },
    };
  }

  throw new Error((res.message as string) || fallbackMessage);
}
